import { createStore, type StoreApi } from 'zustand/vanilla';
import {
  TipoNota,
  type AcompanhamentoDto,
  type AlterarNotaEntrada,
  type ConectorDto,
  type CriarConectorEntrada,
  type CriarNotaQuadroEntrada,
  type MembroQuadroDto,
  type NotaDto,
  type UrlArquivoDto,
} from '@nexital/shared';
import { MENSAGEM_SETA_SEM_POST_IT, MENSAGEM_SO_O_DONO_EXCLUI, setaPermitida } from '../domain/notas';
import { ErroApi } from './portas';
import type { ApiNotas } from './portas-notas';
import type { Salvamento } from './quadro-store';

export type EstadoNotas = {
  /** Post-its, textos e (na licitação) notas de lista do lugar. */
  notas: NotaDto[];
  setas: ConectorDto[];
  salvamento: Salvamento;
  gravacoesPendentes: number;
  aviso: string | null;
  /** Pessoas do quadro (para compartilhar) e se o usuário pode tornar nota pública; quem integra informa. */
  pessoasQuadro: MembroQuadroDto[];
  podeTornarPublica: boolean;
  /** Store de uma licitação: os post-its dela estão dentro do cartão (RQ-NOT-008). */
  dentroDaLicitacao: boolean;
  configurar(opcoes: { pessoasQuadro: MembroQuadroDto[]; podeTornarPublica: boolean }): void;
  carregar(): Promise<void>;
  /** Post-it ou texto com posição, no quadro ou no quadro da licitação. */
  criarNota(entrada: CriarNotaQuadroEntrada): Promise<boolean>;
  alterarNota(uuid: string, mudanca: Omit<AlterarNotaEntrada, 'versao'>): Promise<boolean>;
  excluirNota(uuid: string): Promise<boolean>;
  criarSeta(entrada: CriarConectorEntrada): Promise<boolean>;
  excluirSeta(uuid: string): Promise<boolean>;
  /** Nota de lista da licitação (pública, RQ-NOT-002). Só no store da licitação. */
  criarNotaDeLista(texto: string): Promise<boolean>;
  /** Gestor e administrador: de quem podem acompanhar as notas privadas; null para os demais perfis. */
  obterAcompanhamento(): Promise<AcompanhamentoDto | null>;
  /** Marca quem acompanhar e relê as notas (as acompanhadas aparecem só para leitura). */
  definirAcompanhamento(uuids: string[]): Promise<boolean>;
  /** Anexa um arquivo à nota (envio direto ao S3) e relê. */
  anexar(notaUuid: string, arquivo: File): Promise<boolean>;
  urlAnexo(notaUuid: string, arquivoUuid: string): Promise<UrlArquivoDto | null>;
  excluirAnexo(notaUuid: string, arquivoUuid: string): Promise<boolean>;
  dispensarAviso(): void;
};

/** Onde as notas vivem: no quadro ou dentro de uma licitação do quadro. */
export type LugarNotas = { quadroUuid: string; processoUuid?: string };

type Loja = Pick<StoreApi<EstadoNotas>, 'setState' | 'getState'>;
type Ambiente = { api: ApiNotas; lugar: LugarNotas; loja: Loja };

/** Notas e setas de um lugar. Mudanças de nota aparecem antes (otimista); conflito (409) relê do servidor. */
export function criarStoreNotas(api: ApiNotas, lugar: LugarNotas) {
  return createStore<EstadoNotas>()((setState, getState) => {
    const amb: Ambiente = { api, lugar, loja: { setState, getState } };
    const { quadroUuid, processoUuid } = lugar;
    return {
      notas: [],
      setas: [],
      salvamento: 'salvo',
      gravacoesPendentes: 0,
      aviso: null,
      pessoasQuadro: [],
      podeTornarPublica: false,
      dentroDaLicitacao: Boolean(processoUuid),
      configurar: (opcoes) => setState(opcoes),
      carregar: () => carregar(amb),
      criarNota: (entrada) =>
        gravar(amb, () =>
          processoUuid
            ? api.criarNotaProcesso(quadroUuid, processoUuid, { ...entrada, tipo: entrada.tipo as TipoNota.PostIt | TipoNota.Texto })
            : api.criarNota(quadroUuid, entrada),
        ),
      alterarNota: (uuid, mudanca) => alterarNota(amb, uuid, mudanca),
      excluirNota: (uuid) => excluirNota(amb, uuid),
      criarSeta: (entrada) => {
        if (!setaPermitida(entrada.origem, entrada.destino)) {
          setState({ aviso: MENSAGEM_SETA_SEM_POST_IT });
          return Promise.resolve(false);
        }
        return gravar(amb, () => (processoUuid ? api.criarSetaProcesso(quadroUuid, processoUuid, entrada) : api.criarSeta(quadroUuid, entrada)));
      },
      excluirSeta: (uuid) => excluirSeta(amb, uuid),
      criarNotaDeLista: (texto) =>
        processoUuid ? gravar(amb, () => api.criarNotaProcesso(quadroUuid, processoUuid, { tipo: TipoNota.DoProcesso, texto })) : Promise.resolve(false),
      obterAcompanhamento: () => api.obterAcompanhamento(quadroUuid).catch(() => null),
      definirAcompanhamento: (uuids) => gravar(amb, () => api.definirAcompanhamento(quadroUuid, uuids)),
      anexar: (notaUuid, arquivo) => gravar(amb, () => api.anexar(quadroUuid, notaUuid, arquivo)),
      urlAnexo: (notaUuid, arquivoUuid) => api.urlAnexo(quadroUuid, notaUuid, arquivoUuid).catch(() => null),
      excluirAnexo: (notaUuid, arquivoUuid) => gravar(amb, () => api.excluirAnexo(quadroUuid, notaUuid, arquivoUuid)),
      dispensarAviso: () => setState({ aviso: null }),
    };
  });
}

async function carregar({ api, lugar, loja }: Ambiente) {
  try {
    const { notas, conectores } = lugar.processoUuid
      ? await api.listarDoProcesso(lugar.quadroUuid, lugar.processoUuid)
      : await api.listarDoQuadro(lugar.quadroUuid);
    loja.setState({ notas, setas: conectores });
  } catch (erro) {
    loja.setState({ aviso: (erro as Error).message });
  }
}

/** Grava com o indicador de salvamento (RQ-COL-007) e relê. Devolve se gravou. */
async function gravar(amb: Ambiente, trabalho: () => Promise<unknown>): Promise<boolean> {
  const { loja } = amb;
  loja.setState((s) => ({ gravacoesPendentes: s.gravacoesPendentes + 1, salvamento: 'salvando' }));
  try {
    await trabalho();
    loja.setState((s) => ({ salvamento: s.gravacoesPendentes > 1 ? 'salvando' : 'salvo' }));
    return true;
  } catch (erro) {
    const conflito = erro instanceof ErroApi && erro.status === 409;
    loja.setState({ salvamento: conflito ? 'conflito' : 'erro', aviso: (erro as Error).message });
    return false;
  } finally {
    loja.setState((s) => ({ gravacoesPendentes: s.gravacoesPendentes - 1 }));
    await carregar(amb);
  }
}

/** Aplica na tela antes; a versão nova vem na releitura. */
function alterarNota(amb: Ambiente, uuid: string, mudanca: Omit<AlterarNotaEntrada, 'versao'>) {
  const { loja, api, lugar } = amb;
  const nota = loja.getState().notas.find((n) => n.uuid === uuid);
  if (!nota) return Promise.resolve(false);
  const { compartilharCom: _c, visibilidade: _v, ...visiveis } = mudanca;
  loja.setState((s) => ({ notas: s.notas.map((n) => (n.uuid === uuid ? { ...n, ...visiveis } : n)) }));
  return gravar(amb, () => api.alterarNota(lugar.quadroUuid, uuid, { ...mudanca, versao: nota.versao }));
}

function excluirNota(amb: Ambiente, uuid: string) {
  const { loja, api, lugar } = amb;
  if (loja.getState().notas.find((n) => n.uuid === uuid)?.podeEditar === false) {
    loja.setState({ aviso: MENSAGEM_SO_O_DONO_EXCLUI });
    return Promise.resolve(false);
  }
  loja.setState((s) => ({
    notas: s.notas.filter((n) => n.uuid !== uuid),
    setas: s.setas.filter((c) => c.origem.uuid !== uuid && c.destino.uuid !== uuid),
  }));
  return gravar(amb, () => api.excluirNota(lugar.quadroUuid, uuid));
}

function excluirSeta(amb: Ambiente, uuid: string) {
  const { loja, api, lugar } = amb;
  loja.setState((s) => ({ setas: s.setas.filter((c) => c.uuid !== uuid) }));
  return gravar(amb, () => api.excluirSeta(lugar.quadroUuid, uuid));
}

export type StoreNotas = ReturnType<typeof criarStoreNotas>;
